"use client";

import { create } from "zustand";

// Consentimiento de analítica detallada (Ley 21.719): las visitas se miden
// siempre con GA4 (interés legítimo), pero los eventos de acciones y Clarity
// (grabaciones de sesión) solo si la persona acepta. Se guarda en localStorage
// (no en una cookie) para que el sitio siga siendo estático: el servidor nunca
// lo lee.
// Si cambia lo que medimos de forma sustancial, subir CONSENT_VERSION vuelve
// a preguntar a todos.
const STORAGE_KEY = "cv-analytics-consent";
const CONSENT_VERSION = 1;

export type ConsentChoice = "granted" | "denied";

interface ConsentState {
  // null = todavía no decide (o aún no se leyó localStorage).
  choice: ConsentChoice | null;
  // true una vez leído localStorage: antes de eso no se muestra el banner,
  // para que no parpadee en cada visita de quien ya eligió.
  loaded: boolean;
  // El banner se puede volver a abrir desde el footer ("Preferencias de cookies").
  reopened: boolean;
  load: () => void;
  choose: (choice: ConsentChoice) => void;
  reopen: () => void;
}

function readStored(): ConsentChoice | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { v?: number; choice?: string };
    if (parsed.v !== CONSENT_VERSION) return null;
    return parsed.choice === "granted" || parsed.choice === "denied" ? parsed.choice : null;
  } catch {
    return null;
  }
}

export const useConsentStore = create<ConsentState>((set) => ({
  choice: null,
  loaded: false,
  reopened: false,
  load: () => set({ choice: readStored(), loaded: true }),
  choose: (choice) => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ v: CONSENT_VERSION, choice, at: new Date().toISOString() }),
      );
    } catch {
      // Almacenamiento bloqueado: la elección vale solo para esta visita.
    }
    set({ choice, reopened: false });
  },
  reopen: () => set({ reopened: true }),
}));

// Otros avisos fijos abajo (app Android, sugerencia de idioma) esperan a que
// la persona responda el de cookies, para no apilarse encima.
export function useConsentDecided(): boolean {
  return useConsentStore((s) => s.loaded && s.choice !== null && !s.reopened);
}

// Cookies que deja Clarity: se borran al retirar el consentimiento (las de
// GA4 se mantienen, porque la medición de visitas no depende de él).
const ANALYTICS_COOKIE = /^(_clck|_clsk|CLID|MUID|ANONCHK|MR|SM)$/;

export function deleteAnalyticsCookies(): void {
  try {
    const host = window.location.hostname;
    const domains = ["", host, `.${host}`, `.${host.split(".").slice(-2).join(".")}`];
    for (const pair of document.cookie.split("; ")) {
      const name = pair.split("=")[0];
      if (!ANALYTICS_COOKIE.test(name)) continue;
      for (const domain of domains) {
        document.cookie = `${name}=; path=/; max-age=0${domain ? `; domain=${domain}` : ""}`;
      }
    }
  } catch {
    // Sin acceso a cookies: no hay nada que borrar.
  }
}
